/**
 * Validates that a child route has the required structure
 *
 * @param {object} childRoute - The routes object
 */
const validateChildRoute = (childRoute) => {
	if (typeof childRoute.PARAMS === 'undefined') {
		throw new ReferenceError('Expected child route PARAMS attribute when generating routes.');
	}

	if (typeof childRoute.PARAMS !== 'object') {
		throw new TypeError('Expected child route PARAMS to be an object.');
	}

	if (Object.keys(childRoute.PARAMS).length === 0) {
		throw new SyntaxError('Child route PARAMS cannot be empty.');
	}
};

/**
 * Generates an array of routes to be used by GlobalRouter.
 *
 * @param {object} routesConfig - The routes configuration object
 * @returns {array}
 */
export const generateRoutes = (routesConfig) => {
	const routes = [];
	const validPaths = [];

	const configCopy = Object.assign({}, routesConfig);

	for (const key in configCopy) {
		const route = configCopy[key];
		// Only push active routes
		if (route.ACTIVE) {
			const routeConfig = {
				name: route.NAME,
				path: route.PATH,
				label: route.LABEL,
				layout: route.LAYOUT || 'regular', // Layout value only exists for full or semi layouts
				icon: route.ICON,
				permissions: route.PERMISSIONS,
				isInternal: !!route.INTERNAL,
			};

			//  Create array of child routes then remove them from the config object
			if (!!route.CHILDREN) {
				const childRoutes = [];
				route.CHILDREN.forEach((childRouteName) => {
					const childRoute = configCopy[childRouteName];
					// Only push active children routes
					if (childRoute.ACTIVE) {
						validateChildRoute(childRoute);
						// Build final route using route parameters
						let generatedPath = route.PATH.split(/\/:/);
						generatedPath.forEach((item, index) => {
							if (typeof childRoute.PARAMS[item] !== 'undefined') {
								generatedPath[index] = childRoute.PARAMS[item];
							}
						});
						generatedPath = generatedPath.join('/');
						childRoutes.push({
							name: childRoute.NAME,
							path: generatedPath,
							label: childRoute.LABEL,
							layout: childRoute.LAYOUT || 'regular',
							permissions: route.PERMISSIONS,
							isInternal: !!route.INTERNAL,
						});

						delete configCopy[childRouteName];
					}
				});

				if (childRoutes.length === 0) {
					throw new Error('Child route array returned empty. Have you set ACTIVE param to true?');
				}
				routeConfig.children = childRoutes;
				// If DISPLAY_CHILDREN is present, use its value, otherwise child routes are enabled by default
				routeConfig.showChildren =
					typeof route.DISPLAY_CHILDREN !== 'undefined' ? route.DISPLAY_CHILDREN : true;
			}
			// We never care to validate that Root path is valid
			if (route.PATH !== '/') {
				validPaths.push(route.PATH);
			}

			routes.push(routeConfig);
			delete configCopy[key];
		} else if (!!route.CHILDREN) {
			// Straight up remove any child routes related to non active routes
			route.CHILDREN.forEach((routeName) => delete configCopy[routeName]);
		}
	}

	return {
		routes,
		validPaths,
	};
};

/**
 * Generates an array of routes to be used by GlobalRouter.
 *
 * @param {object} GlobalRouter - Global router instance
 * @param {array} roles - List of the user roles
 * @returns {array}
 */
export const shouldRedirectUser = (GlobalRouter, roles) => {
	return (
		!GlobalRouter.isValidRoute(window.location.pathname) ||
		!GlobalRouter.canAccessRoute(window.location.pathname, roles)
	);
};

/**
 * Retrieves a route config and stores it into the store
 *
 * @param {object} GlobalStore - Global store instance
 * @param {object} GlobalRouter - Global router instance
 * @param {object} STORE_ACTIONS - Store actions enumerable
 */
export const storeCurrentRoute = (GlobalStore, GlobalRouter, STORE_ACTIONS) => {
	GlobalStore.dispatch(
		STORE_ACTIONS.setCurrentRoute(GlobalRouter.getRouteFromPath(window.location.pathname))
	);
};

/**
 * Utility function to flatten all routes into an single array
 * @param {array} roles - List of the user roles
 * @param {array} permissions - Permission for a specific route
 * @returns {boolean} = Returns true if there is at least one role in the list of permissions
 */
export const isRoleInPermissions = (roles, permissions) => {
	return roles.some((role) => permissions.includes(role));
};
